import './AddTeamMemberForm.css';
import React from 'react';
import Input from '../../Input/Input';
import { useLoader } from 'src/provider/LoaderProvider';
import { useCreateUserMutation } from 'src/services/usersService';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import {
  emailScheme,
  passwordConfirmScheme,
  passwordScheme,
  stringScheme,
} from 'src/utils/validation-schemes';
import { Button } from 'src/components/ui/button';
import type { User } from 'src/services/usersService';
import { UserNameFields, UserPasswordFields } from 'src/components/UserFormFields/UserFormFields';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const AddTeamMemberForm = ({
  onSubmit,
}: {
  // Called with the newly created user.
  onSubmit?: (user: User) => void;
}) => {
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [createUser, { isLoading: isLoadingCreateUser }] = useCreateUserMutation();
  const loader = useLoader();

  const { handleSubmit, control, trigger, setError, clearErrors } = useForm({
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      password2: '',
    },
    resolver: yupResolver(
      object({
        email: emailScheme({ required: true }).label('Email'),
        first_name: stringScheme({ required: true }).label('First name'),
        last_name: stringScheme({ required: false }).label('Last name'),
        password: passwordScheme({
          required: true,
          min: 8,
          minMessage: 'Min. 8 characters',
        }).label('Password'),
        password2: passwordConfirmScheme({
          required: true,
          min: 8,
          minMessage: 'Min. 8 characters',
        }).label('Confirm password'),
      }),
    ),
  });

  const submit = async (data: {
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    password2: string;
  }) => {
    const isCompanyEmail = await fetch(`/api/is-company-email?email=${data.email}`).then((res) =>
      res.json(),
    );

    if (isCompanyEmail && !isCompanyEmail.result) {
      setError('email', {
        message: 'Invalid',
      });
      setEmailErrorMessage('Please use the corporate email.');

      return loader.hide();
    }

    const validationResponse = await fetch(`/api/mailgun-validate?email=${data.email}`).then(
      (res) => res.json(),
    );

    if (validationResponse?.result && !validationResponse.result.isValid) {
      setError('email', { message: 'Invalid' });
      setEmailErrorMessage(validationResponse?.result?.message || '');

      return loader.hide();
    }

    const request = {
      email: data.email,
      password: data.password,
      password2: data.password2,
      first_name: data.first_name,
      last_name: data.last_name,
      role: 'support',
    };

    createUser(request).then((createUserResult) => {
      if (!('error' in createUserResult)) {
        onSubmit?.(createUserResult.data);
      }
    });
  };

  useLoaderWhile(isLoadingCreateUser);

  return (
    <form id="add-team-member-form" onSubmit={handleSubmit(submit)}>
      <UserNameFields control={control} trigger={trigger} />
      <div className={'input-container'}>
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <div className={'relative'}>
              <Input
                type={'email'}
                label={'Email'}
                placeholder={'email@email.com'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onBlur={() => trigger('email')}
                onFocus={() => {
                  clearErrors('email');
                  setEmailErrorMessage('');
                }}
              />
              {Boolean(emailErrorMessage) && (
                <div
                  className={
                    'absolute top-[calc(100%+4px)] w-full bg-error_bg text-error text-center text-sm font-semibold font-jamjuree py-1 px-4 z-10 rounded-sm-5 shadow'
                  }
                >
                  {emailErrorMessage}
                </div>
              )}
            </div>
          )}
        />
      </div>
      <UserPasswordFields control={control} />
      <div className={'input-container buttons'}>
        <Button variant="pill-green" size="pill" type="submit" form="add-team-member-form">
          Add User
        </Button>
      </div>
    </form>
  );
};
